(() => {
  if (window.rrBrandCarouselLoaded) return;
  window.rrBrandCarouselLoaded = true;
  const instances = new WeakMap();
  const find = scope => [...(scope.matches?.('[data-brand-carousel]') ? [scope] : []), ...scope.querySelectorAll('[data-brand-carousel]')];
  const initialize = section => {
    if (instances.has(section)) return;
    const viewport = section.querySelector('[data-brand-viewport]');
    const track = section.querySelector('[data-brand-track]');
    const slides = [...(track?.querySelectorAll('[data-brand-slide]') || [])];
    if (!viewport || !track || !slides.length) return;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let frame = 0, lastTime = 0, position = 0, expected = 0, period = 0, start = 0;
    let manualUntil = 0, pointerDown = false, keyboardFocus = false, destroyed = false, pendingMeasure = false;
    let onscreen = !('IntersectionObserver' in window);
    const enabled = () => !preference.matches && !window.Shopify?.designMode && slides.length > 1;
    const modulo = value => ((value % period) + period) % period;
    const write = value => { position = value; viewport.scrollLeft = value; expected = viewport.scrollLeft; };
    const clone = slide => {
      const copy = slide.cloneNode(true);
      copy.dataset.brandClone = '';
      copy.removeAttribute('data-brand-slide');
      copy.setAttribute('aria-hidden', 'true');
      [copy, ...copy.querySelectorAll('*')].forEach(node => {
        node.removeAttribute('id');
        node.removeAttribute('data-shopify-editor-block');
        node.removeAttribute('data-shopify-editor-section');
        if (node.matches('a,button,input,select,textarea,[tabindex]')) node.tabIndex = -1;
        if (node.matches('img')) node.loading = 'eager';
      });
      return copy;
    };
    const measure = () => {
      if (pointerDown || performance.now() < manualUntil || keyboardFocus) { pendingMeasure = true; return; }
      pendingMeasure = false;
      const phase = period > 0 ? modulo(viewport.scrollLeft - start) / period : 0;
      track.querySelectorAll('[data-brand-clone]').forEach(copy => copy.remove());
      period = 0; start = 0;
      if (enabled() && viewport.clientWidth > 0) {
        const firstCopy = clone(slides[0]);
        track.append(firstCopy);
        period = firstCopy.offsetLeft - slides[0].offsetLeft;
        firstCopy.remove();
        if (period > 0) {
          const copies = Math.ceil(viewport.clientWidth / period) + 1;
          const leading = document.createDocumentFragment();
          const trailing = document.createDocumentFragment();
          for (let index = 0; index < copies; index++) slides.forEach(slide => { leading.append(clone(slide)); trailing.append(clone(slide)); });
          track.prepend(leading); track.append(trailing);
          start = slides[0].offsetLeft;
          write(start + phase * period);
        }
      } else write(0);
      sync();
    };
    const tick = now => {
      frame = 0;
      if (destroyed || !onscreen || document.hidden || !enabled()) { lastTime = 0; return; }
      const delta = lastTime ? Math.min(now - lastTime, 50) : 0;
      lastTime = now;
      if (!pointerDown && !keyboardFocus && now >= manualUntil) {
        if (pendingMeasure) measure();
        if (period > 0) {
          if (Math.abs(viewport.scrollLeft - expected) > 1) position = viewport.scrollLeft;
          write(start + modulo(position - start + delta * 0.028));
        }
      } else position = viewport.scrollLeft;
      if (!frame) frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame); frame = 0; lastTime = 0;
      if (!destroyed && onscreen && !document.hidden && enabled()) frame = requestAnimationFrame(tick);
    };
    const interact = () => { manualUntil = performance.now() + 220; };
    const hold = () => { pointerDown = true; keyboardFocus = false; interact(); };
    const release = () => { if (pointerDown) { pointerDown = false; interact(); } };
    viewport.addEventListener('mousedown', event => { if (event.button === 0 && event.target.closest('[data-brand-clone] a')) event.preventDefault(); }, options);
    viewport.addEventListener('pointerdown', event => { if (event.pointerType !== 'touch') hold(); }, options);
    window.addEventListener('pointerup', event => { if (event.pointerType !== 'touch') release(); }, options);
    window.addEventListener('pointercancel', event => { if (event.pointerType !== 'touch') release(); }, options);
    viewport.addEventListener('touchstart', hold, { ...options, passive: true });
    window.addEventListener('touchend', event => { if (!event.touches.length) release(); }, { ...options, passive: true });
    window.addEventListener('touchcancel', release, { ...options, passive: true });
    viewport.addEventListener('wheel', interact, { ...options, passive: true });
    viewport.addEventListener('scroll', () => {
      if (Math.abs(viewport.scrollLeft - expected) > 1) { position = viewport.scrollLeft; interact(); }
    }, { ...options, passive: true });
    viewport.addEventListener('keydown', () => { keyboardFocus = true; }, options);
    viewport.addEventListener('focusin', event => { keyboardFocus = event.target.matches(':focus-visible'); }, options);
    viewport.addEventListener('focusout', () => queueMicrotask(() => {
      if (!viewport.contains(document.activeElement)) { keyboardFocus = false; interact(); }
    }), options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) pointerDown = false; sync(); }, options);
    preference.addEventListener('change', () => { keyboardFocus = false; pointerDown = false; manualUntil = 0; measure(); }, options);
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(viewport);
    else window.addEventListener('resize', measure, options);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => { onscreen = entries[0].isIntersecting; sync(); }, { threshold: 0.1 }) : null;
    observer?.observe(section);
    instances.set(section, {
      select: slide => { keyboardFocus = true; write(slide.offsetLeft); },
      destroy: () => { destroyed = true; cancelAnimationFrame(frame); controller.abort(); resizeObserver?.disconnect(); observer?.disconnect(); track.querySelectorAll('[data-brand-clone]').forEach(copy => copy.remove()); }
    });
    measure();
  };
  const initializeWithin = scope => find(scope).forEach(initialize);
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => find(event.target).forEach(section => { instances.get(section)?.destroy(); instances.delete(section); }));
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-brand-slide]');
    const section = slide?.closest('[data-brand-carousel]');
    if (section) { initialize(section); instances.get(section)?.select(slide); }
  });
})();
